import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import Picker, { PickerValue } from "react-mobile-picker"
import moment from "moment-jalaali"
import { UseFormRegister, UseFormSetValue } from "react-hook-form"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { monthsList, getYearsRange, getMonthsRange, getDatesRange } from "utils/dateUtils"
import CalendarIcon from "components/atoms/_icons/CalendarIcon"
import FormField from "components/atoms/FormField"
import CTA from "components/atoms/CTA"
import BottomSheet from "components/atoms/BottomSheet"

type CalendarPropsType = {
  id: string;
  label: string;
  value: string;
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  onChange?: (e: any) => void;
}

moment.loadPersian({ usePersianDigits: false })

export default function Calendar(props: CalendarPropsType) {
  const {
    id,
    label,
    value,
    register,
    setValue,
    onChange = new Function()
  } = props

  const [showPicker, setShowPicker] = useState(false)
  const [yearsRange, setYearsRange] = useState<string[]>([])
  const [monthsRange, setMonthsRange] = useState<string[]>([])
  const [datesRange, setDatesRange] = useState<string[]>([])
  const [valueToRender, setValueToRender] = useState<string>("")
  const [selectedDateValue, setSelectedDateValue] = useState<PickerValue>({
    year: "",
    month: "",
    day: ""
  })

  const { t } = useTranslation()

  const handlePickerChange = useCallback((newValue: PickerValue, key: string) => {
    if (key === "day") {
      setSelectedDateValue(newValue)
      return
    }

    const currentJalaliDate = moment(new Date())
    const dates = getDatesRange(currentJalaliDate, selectedDateValue)
    setDatesRange(dates)

    const newDay = dates.includes(newValue.day) ? newValue.day : dates[dates.length - 1]
    setSelectedDateValue({ ...newValue, day: newDay })
  }, [])

  useEffect(() => {
    if (value) {
      setValueToRender(formatValueToRender(new Date(value)))
      setValue(id, value, { shouldDirty: true })
    } else {
      setValueToRender("")
      setValue(id, "", { shouldDirty: true })
    }
  }, [value])

  useEffect(() => {
    updateSelectedDateValue()

    const currentJalaliDate = moment(new Date())
    const years = getYearsRange(currentJalaliDate.jYear())
    setYearsRange(years)
  }, [showPicker])

  useEffect(() => {
    const currentJalaliDate = moment(new Date())
    const months = getMonthsRange(currentJalaliDate, selectedDateValue)
    const dates = getDatesRange(currentJalaliDate, selectedDateValue)
    setMonthsRange(months)
    setDatesRange(dates)
  }, [selectedDateValue])

  function updateSelectedDateValue() {
    const date = value ? new Date(value) : new Date()
    const jalaliDate = moment(date)
    setSelectedDateValue({
      ...selectedDateValue,
      year: `${jalaliDate.jYear()}`,
      month: `${jalaliDate.jMonth() + 1}`.padStart(2, "0"),
      day: `${jalaliDate.jDate()}`.padStart(2, "0")
    })
  }

  function handleSubmit() {
    const jalaliDate = moment(`${selectedDateValue.year}/${selectedDateValue.month}/${selectedDateValue.day}`, "jYYYY/jM/jD")
    const isoStingDateTime = jalaliDate.format("YYYY-MM-DDT00:00:00.000") + "Z"
    const dateObject = new Date(isoStingDateTime)
    onChange(isoStingDateTime)
    setValueToRender(formatValueToRender(dateObject))
    setShowPicker(false)
  }

  function formatValueToRender(value: Date) {
    return moment(value).format("jYYYY/jM/jD")
  }

  function handleDiscard() {
    setShowPicker(false)
  }

  function toggleShowBottomSheet() {
    setShowPicker(!showPicker)
  }

  function resetValue() {
    onChange("")
    setValueToRender("")
    setSelectedDateValue({
      year: "",
      month: "",
      day: ""
    })
  }

  return (
    <>
      <Container
        id={id}
        {...register(id)}
      >
        <FormField
          id={`${id}-field`}
          label={label}
          value={valueToRender}
          icon={<CalendarIcon/>}
          isDeletable={true}
          onClick={toggleShowBottomSheet}
          onDeleteIconClick={resetValue}
        />
      </Container>
      <BottomSheet
        id="picker"
        show={showPicker}
        swipeable={false}
        openBottomSheet={() => {
        }}
        closeBottomSheet={toggleShowBottomSheet}
      >
        <Picker
          value={selectedDateValue}
          onChange={handlePickerChange}
          height={250}
          itemHeight={50}
          wheelMode="natural"
        >
          <Picker.Column name="day" id="date" className="picker-column">
            {datesRange.map((day) => (
              <Picker.Item key={day} value={day} className="picker-item">
                {({ selected }) => (
                  <div className={selected ? "selected farsi-digits" : "farsi-digits"}>{day}</div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          <Picker.Column name="month" id="month" className="picker-column">
            {monthsRange.map((month) => (
              <Picker.Item key={month} value={month} className="picker-item">
                {({ selected }) => (
                  <div className={selected ? "selected farsi-digits" : "farsi-digits"}>{monthsList[parseInt(month) - 1]}</div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>

          <Picker.Column name="year" id="year" className="picker-column">
            {yearsRange.map((year) => (
              <Picker.Item key={year} value={year} className="picker-item">
                {({ selected }) => (
                  <div className={selected ? "selected farsi-digits" : "farsi-digits"}>{year}</div>
                )}
              </Picker.Item>
            ))}
          </Picker.Column>
        </Picker>

        <ButtonsWrapper>
          <CTA
            variant="contained"
            onClick={handleSubmit}
          >
            {t("general.submit")}
          </CTA>
          <CTA
            variant="outlined"
            onClick={handleDiscard}
          >
            {t("general.discard")}
          </CTA>
        </ButtonsWrapper>
      </BottomSheet>
    </>
  )
}

const Container = styled("div")(({ theme }) => ({
  width: "100%"
}))

const ButtonsWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  alignItems: "center",
  gap: "24px",
  marginTop: "24px"
}))

